import React, { useState, useEffect } from 'react';
import { X, User, Loader2, UserCheck, Sparkles, AlertTriangle, TrendingUp, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

/**
 * Assign Task Modal - AI-Enhanced with Workload Intelligence
 */
const AssignTaskModal = ({ caseId, task, onClose, onAssign }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [memberWorkloads, setMemberWorkloads] = useState({});
  const [estimateAdjustment, setEstimateAdjustment] = useState(null);

  useEffect(() => {
    fetchCaseMembers();
    fetchAISuggestion();
  }, [caseId]);

  useEffect(() => {
    if (members.length > 0) {
      fetchMemberWorkloads();
    }
  }, [members]);

  const fetchCaseMembers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/cases/${caseId}/members`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch members:', error);
      toast.error('Kunne ikke laste teammedlemmer');
    } finally {
      setLoading(false);
    }
  };

  const fetchAISuggestion = async () => {
    setLoadingAI(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/ai/tasks/suggest-assignee`,
        {
          case_id: caseId,
          task_title: task.title,
          task_description: task.description || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiSuggestion(response.data);
      
      // Auto-select AI suggestion
      if (response.data.suggested_assignee) {
        setSelectedUserId(response.data.suggested_assignee.user_id);
      }
    } catch (error) {
      console.error('AI suggestion error:', error);
      // Silent fail
    } finally {
      setLoadingAI(false);
    }
  };

  const fetchMemberWorkloads = async () => {
    try {
      const token = localStorage.getItem('token');
      const workloadPromises = members.map(async (member) => {
        try {
          // Get all tasks for the case
          const response = await axios.get(
            `${API}/tasks/${caseId}?filter=all`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          // Filter to this member's tasks
          const allTasks = response.data.tasks || [];
          const tasks = allTasks.filter(t => 
            t.assignee_user_id === member.user_id && 
            ['TODO', 'IN_PROGRESS'].includes(t.status)
          );
          const totalHours = tasks.reduce((sum, t) => sum + (t.estimated_hours || 0), 0);
          return {
            user_id: member.user_id,
            active_tasks: tasks.length,
            total_hours: totalHours
          };
        } catch (err) {
          return {
            user_id: member.user_id,
            active_tasks: 0,
            total_hours: 0
          };
        }
      });
      
      const workloads = await Promise.all(workloadPromises);
      const workloadMap = {};
      workloads.forEach(w => {
        workloadMap[w.user_id] = w;
      });
      setMemberWorkloads(workloadMap);
    } catch (error) {
      console.error('Failed to fetch workloads:', error);
    }
  };

  const handleAssign = async () => {
    if (!selectedUserId) {
      toast.error('Velg et teammedlem');
      return;
    }

    setAssigning(true);
    try {
      await onAssign(selectedUserId);
    } catch (error) {
      // Error handled in parent
    } finally {
      setAssigning(false);
    }
  };

  const getWorkloadStatus = (workload) => {
    if (!workload) return 'unknown';
    const hours = workload.total_hours;
    if (hours > 40) return 'overloaded';
    if (hours > 20) return 'busy';
    if (hours > 10) return 'moderate';
    return 'available';
  };

  const getWorkloadBadge = (status) => {
    const badges = {
      overloaded: { label: 'Overbelastet', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
      busy: { label: 'Høy belastning', color: 'bg-orange-100 text-orange-700', icon: TrendingUp },
      moderate: { label: 'Moderat', color: 'bg-yellow-100 text-yellow-700', icon: User },
      available: { label: 'Ledig', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      unknown: { label: 'Ukjent', color: 'bg-gray-100 text-gray-600', icon: User }
    };
    return badges[status] || badges.unknown;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              Tildel oppgave
              {loadingAI && <Loader2 className="w-4 h-4 animate-spin text-gray-600" />}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-2">{task.title}</p>
          {task.estimated_hours && (
            <p className="text-xs text-gray-500 mt-1">Estimert: {task.estimated_hours}t</p>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* AI Suggestion */}
          {aiSuggestion?.suggested_assignee && (
            <div className="mb-4 p-3 bg-gray-50 border border-indigo-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-indigo-900 mb-1">
                    AI anbefaler: {aiSuggestion.suggested_assignee.user_name}
                  </p>
                  <p className="text-xs text-indigo-700">
                    {aiSuggestion.reasoning}
                  </p>
                  {aiSuggestion.workload_info && (
                    <p className="text-xs text-indigo-600 mt-1">
                      {aiSuggestion.workload_info.active_tasks} aktive oppgaver, {aiSuggestion.workload_info.estimated_hours}t
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">Ingen teammedlemmer funnet</p>
            </div>
          ) : (
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Velg teammedlem
              </label>
              {members.map((member) => {
                const workload = memberWorkloads[member.user_id];
                const workloadStatus = getWorkloadStatus(workload);
                const badge = getWorkloadBadge(workloadStatus);
                const Icon = badge.icon;
                const isAISuggested = aiSuggestion?.suggested_assignee?.user_id === member.user_id;

                return (
                  <button
                    key={member.user_id}
                    onClick={() => setSelectedUserId(member.user_id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      selectedUserId === member.user_id
                        ? 'border-indigo-600 bg-gray-50'
                        : isAISuggested
                        ? 'border-indigo-300 bg-gray-50/50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-gray-900">{member.user_name}</p>
                          {isAISuggested && (
                            <Sparkles className="w-4 h-4 text-indigo-600" />
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{member.user_email}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                            {member.role}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${badge.color}`}>
                            <Icon className="w-3 h-3" />
                            {badge.label}
                          </span>
                        </div>
                        {workload && (
                          <p className="text-xs text-gray-500 mt-1">
                            {workload.active_tasks} aktive oppgaver • {workload.total_hours.toFixed(1)}t totalt
                          </p>
                        )}
                      </div>
                      {selectedUserId === member.user_id && (
                        <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <UserCheck className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleAssign}
              disabled={!selectedUserId || assigning}
              className="flex-1 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {assigning ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Tildeler...
                </>
              ) : (
                <>
                  <UserCheck className="w-5 h-5" />
                  Tildel
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors"
            >
              Avbryt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignTaskModal;
